import React, { useState } from 'react';
import { Activity, ArrowDown, ArrowUp, ChevronRight, Plus, Settings, Zap } from 'lucide-react';

import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';

/**
 * Design system showcase — every primitive in every variant.
 *
 * Dual purpose:
 *   1. Developer reference. When building a new page, browse this to find
 *      the right component instead of inventing one.
 *   2. Visual regression canary. If something looks "off" here, the token
 *      system or a primitive is broken — easier to spot in isolation than
 *      across 40 pages.
 *
 * Route: /design (admin/dev only — not in the production navbar).
 */

const Section: React.FC<{ title: string; description?: string; children: React.ReactNode }> = ({
  title,
  description,
  children,
}) => (
  <section className="space-y-4">
    <div>
      <h2 className="text-lg font-semibold tracking-tight">{title}</h2>
      {description ? <p className="mt-1 text-sm text-muted-foreground">{description}</p> : null}
    </div>
    <div className="rounded-lg border border-border bg-card p-6">{children}</div>
  </section>
);

const Swatch: React.FC<{ label: string; className: string; foreground?: string }> = ({
  label,
  className,
  foreground = 'text-foreground',
}) => (
  <div className="flex flex-col gap-1.5">
    <div className={`h-16 rounded-md border border-border ${className}`} />
    <div className={`font-mono text-xs ${foreground}`}>{label}</div>
  </div>
);

const Design: React.FC = () => {
  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <div className="container mx-auto max-w-6xl space-y-10 px-6 py-10">
      {/* Header */}
      <div className="space-y-2">
        <div className="flex items-baseline justify-between">
          <h1 className="text-3xl font-bold tracking-tight">Design System</h1>
          <Badge variant="outline" className="font-mono">
            v0 · spike
          </Badge>
        </div>
        <p className="max-w-2xl text-sm text-muted-foreground">
          Tokens + primitives for the SignalDeck refresh. Violet accent · slate/zinc neutrals · Inter / JetBrains Mono ·
          Radix UI under the hood.
        </p>
      </div>

      {/* ── Color tokens ── */}
      <Section title="Color tokens" description="Every color in the system. No others should appear in production.">
        <div className="space-y-6">
          <div>
            <div className="mb-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Surfaces</div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Swatch label="background" className="bg-background" />
              <Swatch label="surface" className="bg-surface" />
              <Swatch label="surface-muted" className="bg-surface-muted" />
              <Swatch label="surface-subtle" className="bg-surface-subtle" />
            </div>
          </div>
          <div>
            <div className="mb-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Brand + ink</div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Swatch label="accent" className="bg-accent" foreground="text-accent" />
              <Swatch label="accent-subtle" className="bg-accent-subtle" />
              <Swatch label="primary" className="bg-primary" />
              <Swatch label="foreground" className="bg-foreground" />
            </div>
          </div>
          <div>
            <div className="mb-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Semantic</div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Swatch label="positive" className="bg-positive" foreground="text-positive" />
              <Swatch label="negative" className="bg-negative" foreground="text-negative" />
              <Swatch label="warning" className="bg-warning" foreground="text-warning" />
              <Swatch label="muted-foreground" className="bg-muted-foreground" />
            </div>
          </div>
        </div>
      </Section>

      {/* ── Typography ── */}
      <Section title="Typography" description="Inter for UI · JetBrains Mono for tabular numerics & code.">
        <div className="space-y-4">
          <div className="space-y-1">
            <div className="text-4xl font-bold tracking-tight">Display · 36px / 700</div>
            <div className="text-2xl font-semibold">Heading · 24px / 600</div>
            <div className="text-lg font-semibold">Subheading · 18px / 600</div>
            <div className="text-base">Body · 16px / 400</div>
            <div className="text-sm">Small · 14px / 400</div>
            <div className="text-xs text-muted-foreground">Caption · 12px / 400 · muted</div>
          </div>
          <div className="rounded-md bg-surface-muted p-4 font-mono text-sm tabular-nums">
            <div className="text-muted-foreground">// Tabular numerics (JetBrains Mono)</div>
            <div>$711.42 → $710.18 · -$1.24 · -0.17%</div>
            <div>+$3,201.50 · 12,409 shares · 56.7% WR</div>
          </div>
        </div>
      </Section>

      {/* ── Buttons ── */}
      <Section title="Button" description="One primary CTA per screen. Use `accent` sparingly.">
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Button>Default</Button>
            <Button variant="accent">Accent (primary CTA)</Button>
            <Button variant="secondary">Secondary</Button>
            <Button variant="outline">Outline</Button>
            <Button variant="ghost">Ghost</Button>
            <Button variant="link">Link</Button>
            <Button variant="destructive">Destructive</Button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm">Small</Button>
            <Button>Default</Button>
            <Button size="lg">Large</Button>
            <Button size="icon" variant="outline" aria-label="settings">
              <Settings className="h-4 w-4" />
            </Button>
            <Button>
              <Plus className="h-4 w-4" />
              With icon
            </Button>
            <Button disabled>Disabled</Button>
          </div>
        </div>
      </Section>

      {/* ── Badges ── */}
      <Section title="Badge" description="Status pills. Semantic variants only for P&L / system status.">
        <div className="flex flex-wrap gap-2">
          <Badge>Default</Badge>
          <Badge variant="secondary">Secondary</Badge>
          <Badge variant="accent">Accent</Badge>
          <Badge variant="outline">Outline</Badge>
          <Badge variant="positive">+$142 · 1.8%</Badge>
          <Badge variant="negative">-$54 · -0.7%</Badge>
          <Badge variant="warning">Stale data</Badge>
        </div>
      </Section>

      {/* ── Cards ── */}
      <Section title="Card" description="Composition primitives. Borders > shadows for hierarchy.">
        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>SPY · Python rules</CardTitle>
              <CardDescription>2-min cadence · 1 round-trip today</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-baseline gap-2">
                <span className="font-mono text-2xl font-semibold tabular-nums">+$15.42</span>
                <Badge variant="positive">+0.13%</Badge>
              </div>
              <div className="mt-2 text-sm text-muted-foreground">Entry $710.50 · current $710.78</div>
            </CardContent>
            <CardFooter className="border-t border-border pt-4">
              <Button variant="ghost" size="sm">
                View details
                <ChevronRight className="h-4 w-4" />
              </Button>
            </CardFooter>
          </Card>
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Circuit breaker</CardTitle>
                <Badge variant="warning">Armed</Badge>
              </div>
              <CardDescription>3 consecutive losses · 47m remaining</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <Activity className="h-4 w-4 text-muted-foreground" />
                Floor temporarily raised from 30 → 50
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Zap className="h-4 w-4 text-muted-foreground" />
                Next entry requires confidence ≥ 50
              </div>
            </CardContent>
          </Card>
        </div>
      </Section>

      {/* ── Table ── */}
      <Section title="Table" description="Dense, enterprise-flavored. Header in muted gray, hover row highlight.">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Time</TableHead>
              <TableHead>Symbol</TableHead>
              <TableHead>Side</TableHead>
              <TableHead className="text-right">Qty</TableHead>
              <TableHead className="text-right">Entry</TableHead>
              <TableHead className="text-right">Exit</TableHead>
              <TableHead className="text-right">P&L</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell className="font-mono text-sm tabular-nums">10:28 AM</TableCell>
              <TableCell className="font-mono font-medium">SPY</TableCell>
              <TableCell>
                <Badge variant="outline">
                  <ArrowUp className="mr-1 h-3 w-3" />
                  LONG
                </Badge>
              </TableCell>
              <TableCell className="text-right font-mono tabular-nums">27</TableCell>
              <TableCell className="text-right font-mono tabular-nums">$711.42</TableCell>
              <TableCell className="text-right font-mono tabular-nums">$711.59</TableCell>
              <TableCell className="text-right font-mono font-semibold tabular-nums text-positive">+$4.59</TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-mono text-sm tabular-nums">10:14 AM</TableCell>
              <TableCell className="font-mono font-medium">SPY</TableCell>
              <TableCell>
                <Badge variant="outline">
                  <ArrowDown className="mr-1 h-3 w-3" />
                  SHORT
                </Badge>
              </TableCell>
              <TableCell className="text-right font-mono tabular-nums">14</TableCell>
              <TableCell className="text-right font-mono tabular-nums">$711.42</TableCell>
              <TableCell className="text-right font-mono tabular-nums">$711.25</TableCell>
              <TableCell className="text-right font-mono font-semibold tabular-nums text-negative">-$2.38</TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-mono text-sm tabular-nums">10:02 AM</TableCell>
              <TableCell className="font-mono font-medium">SPY</TableCell>
              <TableCell>
                <Badge variant="outline">
                  <ArrowUp className="mr-1 h-3 w-3" />
                  LONG
                </Badge>
              </TableCell>
              <TableCell className="text-right font-mono tabular-nums">27</TableCell>
              <TableCell className="text-right font-mono tabular-nums">$711.41</TableCell>
              <TableCell className="text-right font-mono tabular-nums">$711.42</TableCell>
              <TableCell className="text-right font-mono font-semibold tabular-nums text-muted-foreground">
                $0.27
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Section>

      {/* ── Input + Select ── */}
      <Section title="Form controls" description="Input · Select.">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <label htmlFor="symbol-input" className="text-sm font-medium">
              Symbol
            </label>
            <Input id="symbol-input" placeholder="e.g. AAPL NVDA META" />
            <p className="text-xs text-muted-foreground">Comma- or space-separated.</p>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Account mode</label>
            <Select defaultValue="paper">
              <SelectTrigger>
                <SelectValue placeholder="Select mode" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>Alpaca</SelectLabel>
                  <SelectItem value="paper">Paper trading</SelectItem>
                  <SelectItem value="live">Live trading</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Section>

      {/* ── Tabs ── */}
      <Section title="Tabs" description="Inline navigation within a page.">
        <Tabs defaultValue="overview" className="w-full">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="trades">Trades</TabsTrigger>
            <TabsTrigger value="edge">Edge map</TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
          </TabsList>
          <TabsContent value="overview" className="rounded-md border border-border p-4 text-sm">
            Overview tab content — summary metrics, position card, last decision.
          </TabsContent>
          <TabsContent value="trades" className="rounded-md border border-border p-4 text-sm">
            Trades tab content — recent round-trips table.
          </TabsContent>
          <TabsContent value="edge" className="rounded-md border border-border p-4 text-sm">
            Edge map tab content — bucketed setup stats.
          </TabsContent>
          <TabsContent value="settings" className="rounded-md border border-border p-4 text-sm">
            Settings tab content — active config, CB knobs, phase floors.
          </TabsContent>
        </Tabs>
      </Section>

      {/* ── Dialog ── */}
      <Section title="Dialog" description="Modal for confirmations and forms.">
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button variant="outline">Open dialog</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Close position?</DialogTitle>
              <DialogDescription>
                Close 27 shares of SPY at market. Unrealized P&L:{' '}
                <span className="font-mono text-positive">+$4.59</span>.
              </DialogDescription>
            </DialogHeader>
            <div className="rounded-md bg-surface-muted p-3 text-sm">
              <div className="grid grid-cols-2 gap-y-1 font-mono tabular-nums">
                <span className="text-muted-foreground">Side</span>
                <span>LONG</span>
                <span className="text-muted-foreground">Avg entry</span>
                <span>$711.42</span>
                <span className="text-muted-foreground">Current</span>
                <span>$711.59</span>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={() => setDialogOpen(false)}>
                Close position
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </Section>

      <div className="border-t border-border pt-6 text-xs text-muted-foreground">
        Components live in <code className="font-mono">src/components/ui/</code> · tokens in{' '}
        <code className="font-mono">src/styles/tokens.css</code> · this page at{' '}
        <code className="font-mono">/design</code>.
      </div>
    </div>
  );
};

export default Design;
